import axios from "axios";

/**
 * Utility Funktionen für API Aufrufe
 */
export class ApiUtils {
    static dodoCheckoutEndpoint = "https://n8n.dreamytales.de/webhook/dodo/create-checkout";

    static getShopifyAjaxUrl(endpoint) {
        const shopDomain = window.location.hostname;
        const routeRoot = window.Shopify?.routes?.root || "/";
        const normalizedRoot = routeRoot.endsWith("/") ? routeRoot : `${routeRoot}/`;
        return `https://${shopDomain}${normalizedRoot}${endpoint}`;
    }

    static parseJsonResponse(data) {
        if (typeof data !== "string") {
            return data;
        }

        const trimmedData = data.trim();
        if (!trimmedData) {
            return data;
        }

        try {
            return JSON.parse(trimmedData);
        } catch {
            // sentry-ignore: non-JSON cart response is returned as-is
            return data;
        }
    }

    static getErrorMetadata(error) {
        return {
            code: error?.code || "unknown",
            status: error?.response?.status || "none",
        };
    }

    /**
     * Lädt den aktuellen Shopify Cart
     * @returns {Promise<Object>} - Cart Daten
     */
    static async getCart() {
        try {
            const response = await axios.get(this.getShopifyAjaxUrl("cart.js"), {
                // sentry-ignore: reported with checkout context by cartMixin.captureCheckoutException
                sentryReport: false,
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                }
            });
            
            return this.parseJsonResponse(response.data);
        } catch (error) {
            if (this.isDevMode()) console.error("Error fetching cart", this.getErrorMetadata(error));
            throw error;
        }
    }

    /**
     * Aktualisiert Cart Items (z.B. zum Entfernen)
     * @param {Object} updates - Updates Object mit item keys und quantities
     * @returns {Promise<Object>} - Aktualisierte Cart Daten
     */
    static async updateCart(updates) {
        try {
            const response = await axios.post(this.getShopifyAjaxUrl("cart/update.js"), {
                updates: updates
            }, {
                // sentry-ignore: reported with checkout context by cartMixin.captureCheckoutException
                sentryReport: false,
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                }
            });
            
            return this.parseJsonResponse(response.data);
        } catch (error) {
            if (this.isDevMode()) console.error("Error updating cart", this.getErrorMetadata(error));
            throw error;
        }
    }

    /**
     * Fügt Items zum Cart hinzu
     * @param {Array} items - Array von Cart Items
     * @returns {Promise<Object>} - Response Daten
     */
    static async addToCart(items) {
        try {
            const response = await axios.post(this.getShopifyAjaxUrl("cart/add.js"), {
                items: items,
            }, {
                // sentry-ignore: reported with checkout context by cartMixin.captureCheckoutException
                sentryReport: false,
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                }
            });
            
            return this.parseJsonResponse(response.data);
        } catch (error) {
            if (this.isDevMode()) console.error("Error adding items to cart", this.getErrorMetadata(error));
            throw error;
        }
    }

    /**
     * Erstellt eine Dodo Payments Checkout Session über n8n
     * @param {Object} payload - Dodo Checkout Payload
     * @returns {Promise<Object>} - Dodo Checkout Response
     */
    static async createDodoCheckout(payload) {
        try {
            const response = await axios.post(this.dodoCheckoutEndpoint, payload, {
                // sentry-ignore: reported with checkout context by cartMixin.captureCheckoutException
                sentryReport: false,
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                }
            });

            return response.data;
        } catch (error) {
            if (this.isDevMode()) console.error("Error creating Dodo checkout", this.getErrorMetadata(error));
            throw error;
        }
    }

    /**
     * Redirected zum Checkout
     */
    static redirectToCheckout() {
        const shopDomain = window.location.hostname;
        window.location.href = `https://${shopDomain}/checkout`;
    }

    /**
     * Prüft ob Development Mode aktiv ist
     * @returns {boolean}
     */
    static isDevMode() {
        return process.env.VUE_APP_DEV_MODE === "true";
    }
}

export default ApiUtils; 
